import type { components } from "../../../types/voislog_api";

export type PhrasesSettingData = components["schemas"]["PhrasesSettingData"];

// disp_orderはその文を出す画面の番号。nullならランダム枠、同じ番号が複数あればその中から1つ選ばれる
export type PhraseFormItem = { phrase: string; disp_order: number | null };

export type PhrasesSettingFormItem = {
  // 画面上で並べ替えるための識別子。APIには送らない
  id: string;
  setting_name: string;
  start_time: string;
  end_time: string;
  num_utterance: string;
  phrases: PhraseFormItem[];
};

export type PhraseSettingFormData = {
  minimum_utterances: string;
  phrases_settings: PhrasesSettingFormItem[];
};

export type SettingErrors = Partial<
  Record<"setting_name" | "time" | "num_utterance" | "phrases", string>
>;

// サーバーが保存を受け付けない予約名
export const RESERVED_SETTING_NAME = "system_default";

let nextSettingId = 0;
export function createSettingId(): string {
  nextSettingId += 1;
  return String(nextSettingId);
}

// 新規作成時の読み上げ文の翻訳キー。上から順に画面1から割り当てる
export const DEFAULT_PHRASE_KEYS = [
  "PhraseSetting.defaultPhrase1",
  "PhraseSetting.defaultPhrase2",
  "PhraseSetting.defaultPhrase3",
  "PhraseSetting.defaultPhrase4",
  "PhraseSetting.defaultPhrase5",
  "PhraseSetting.defaultPhrase6",
];

export const EMPTY_PHRASES_SETTING: PhrasesSettingFormItem = {
  id: "",
  setting_name: "",
  start_time: "",
  end_time: "",
  num_utterance: "",
  phrases: [],
};

export function toFormData(
  settingData: PhrasesSettingData,
): PhraseSettingFormData {
  return {
    minimum_utterances: String(settingData.minimum_utterances),
    phrases_settings: settingData.phrases_settings.map((s) => ({
      id: createSettingId(),
      setting_name: s.setting_name,
      // APIは "HH:MM:SS"、入力欄は "HH:MM" なので、比べられるよう "HH:MM" にそろえる
      start_time: (s.start_time ?? "").slice(0, 5),
      end_time: (s.end_time ?? "").slice(0, 5),
      num_utterance: String(s.num_utterance),
      phrases: s.phrases.map((p) => ({
        phrase: p.phrase,
        disp_order: p.disp_order ?? null,
      })),
    })),
  };
}

export function toSettingData(data: PhraseSettingFormData): PhrasesSettingData {
  return {
    minimum_utterances: Number(data.minimum_utterances),
    phrases_settings: data.phrases_settings.map((s) => ({
      setting_name: s.setting_name,
      start_time: s.start_time === "" ? null : s.start_time,
      end_time: s.end_time === "" ? null : s.end_time,
      num_utterance: Number(s.num_utterance),
      // 並び順は配列の順番で表すので使わない
      priority: null,
      phrases: s.phrases.map((p) => ({
        phrase: p.phrase,
        disp_order: p.disp_order,
      })),
    })),
  };
}

export function validateSetting(
  s: PhrasesSettingFormItem,
  minimumUtterances: number,
): SettingErrors {
  const errors: SettingErrors = {};
  if (s.setting_name.trim() === "") {
    errors.setting_name = "PhraseSetting.error.emptySettingName";
  } else if (s.setting_name === RESERVED_SETTING_NAME) {
    errors.setting_name = "PhraseSetting.error.reservedSettingName";
  }
  if (s.start_time !== "" && s.end_time !== "" && s.start_time >= s.end_time) {
    errors.time = "PhraseSetting.error.invalidTimeRange";
  }
  const numUtterance = Number(s.num_utterance);
  if (
    s.num_utterance === "" ||
    numUtterance < minimumUtterances ||
    numUtterance > s.phrases.length
  ) {
    errors.num_utterance = "PhraseSetting.error.numUtteranceRange";
  }
  const fixedScreens = new Set(
    s.phrases.flatMap((p) => (p.disp_order === null ? [] : [p.disp_order])),
  );
  const randomCount = s.phrases.filter((p) => p.disp_order === null).length;
  const emptyScreenCount = numUtterance - fixedScreens.size;
  if (s.phrases.some((p) => p.phrase.trim() === "")) {
    errors.phrases = "PhraseSetting.error.emptyPhrase";
  } else if (s.num_utterance === "") {
    // 画面数が空なら照らし合わせる数が無いので、画面数を前提にした検査はしない
  } else if ([...fixedScreens].some((n) => n > numUtterance)) {
    errors.phrases = "PhraseSetting.error.phraseBeyondScreens";
  } else if (randomCount < emptyScreenCount) {
    errors.phrases = "PhraseSetting.error.insufficientRandomPhrases";
  } else if (emptyScreenCount === 0 && randomCount > 0) {
    // 空いている画面が無いとランダム枠の文は一度も読まれない
    errors.phrases = "PhraseSetting.error.unusedRandomPhrases";
  }
  return errors;
}

// 読み上げ文のエラーの原因になっている文か
export function isInvalidPhrase(
  errorKey: string | undefined,
  p: PhraseFormItem,
  numUtterance: number,
): boolean {
  switch (errorKey) {
    case "PhraseSetting.error.emptyPhrase":
      return p.phrase.trim() === "";
    case "PhraseSetting.error.phraseBeyondScreens":
      return p.disp_order !== null && p.disp_order > numUtterance;
    case "PhraseSetting.error.unusedRandomPhrases":
      return p.disp_order === null;
    default:
      return false;
  }
}
